import { canMenu } from "../lib/menuPermissions";
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, FolderOpen, Sparkles, Table2, Lightbulb, FileDown, FileSpreadsheet,
  Building2, Gavel, MapPin, Eye, FileCode, X, Network,
} from "lucide-react";
import { api, euro, STATUS_LABELS } from "../lib/api";
import FilesTab from "../components/tabs/FilesTab";
import AiProjectLauncher from "../components/ai/AiProjectLauncher";
import AnalysisTab from "../components/tabs/AnalysisTab";
import CmeTab from "../components/tabs/CmeTab";
import ImprovementsTab from "../components/tabs/ImprovementsTab";
import ProjectManifestTab from "../components/tabs/ProjectManifestTab";
import BimViewerTab from "../components/tabs/BimViewerTab";
import { OfferSiteLink } from "../components/cantieri/OfferSiteLink";

const TABS = [
  { id: "file", label: "1. Documenti", icon: FolderOpen },
  { id: "analisi", label: "2. Analisi", icon: Sparkles },
  { id: "manifest", label: "3. Verifica progetto", icon: Network },
  { id: "bimviewer", label: "4. BIM Viewer", icon: Eye },
  { id: "cme", label: "5. Computo e prezzi", icon: Table2 },
  { id: "migliorie", label: "6. Proposte Tecniche Migliorative", icon: Lightbulb },
];

export default function OfferDetail({session}) {
  const canTab = key => key === "file" || canMenu(session?.user,"offers","computo");
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [offer, setOffer] = useState(null);
  const [tab, setTab] = useState(TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "file");
  const [cmeTotal, setCmeTotal] = useState(0);
  const [preview, setPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [customers, setCustomers] = useState([]);

  const loadOffer = async () => {
    try {
      const o = await api.getOffer(id);
      setOffer(o);
    } catch {
      toast.error("Offerta non trovata");
      navigate("/offerte");
    }
  };
  const refreshTotals = async () => {
    const c = await api.getCme(id);
    setCmeTotal(c.totals.total);
  };

  useEffect(() => { loadOffer(); refreshTotals(); api.listCustomers().then(setCustomers).catch(() => setCustomers([])); }, [id]);

  const saveField = async (patch) => {
    const o = await api.updateOffer(id, patch);
    setOffer(o);
  };

  if (!offer) return <div className="text-slate-400 text-sm py-16 text-center">Caricamento…</div>;

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/offerte")} data-testid="back-to-dashboard" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </button>

      <AiProjectLauncher projectType="OFFERTA" sourceId={id}/>
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-md border ${offer.client_type === "gara" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                {offer.client_type === "gara" ? <Gavel className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                {offer.client_type === "gara" ? "Gara d'Appalto" : "Privato"}
              </span>
              {offer.region && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><MapPin className="w-3.5 h-3.5" />{offer.region} · Prezzario ufficiale</span>}
              <OfferSiteLink offer={offer} onChanged={loadOffer} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-display font-extrabold tracking-tight text-slate-900">{offer.title}</h1>
            <p className="text-sm text-slate-500 mt-1">{offer.client_name || "—"} {offer.location && `· ${offer.location}`}</p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="text-right">
              <div className="text-[11px] text-slate-400 uppercase tracking-wide">Totale offerta</div>
              <div className="text-2xl font-display font-bold font-mono text-orange-600">{euro(cmeTotal)}</div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={() => { setPreviewLoading(true); setPreview(true); }} data-testid="preview-pdf-button" className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-sm font-semibold px-3.5 py-2 rounded-lg">
                <Eye className="w-4 h-4" /> Anteprima
              </button>
              <a href={api.exportExcelUrl(id)} data-testid="export-cme-excel-button" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3.5 py-2 rounded-lg">
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </a>
              <a href={api.exportPdfUrl(id)} data-testid="export-summary-pdf-button" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-3.5 py-2 rounded-lg">
                <FileDown className="w-4 h-4" /> PDF
              </a>
              <a href={api.exportDcfUrl(id)} data-testid="export-dcf-button" className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3.5 py-2 rounded-lg">
                <FileCode className="w-4 h-4" /> PriMus DCF
              </a>
            </div>
          </div>
        </div>

        {/* Editable settings row */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-5 pt-5 border-t border-slate-100">
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Nome offerta</label>
            <input
              key={offer.id + "-title-" + offer.title}
              defaultValue={offer.title}
              onBlur={async (e) => {
                const title = e.target.value.trim();
                if (!title || title === offer.title) return;
                try {
                  await saveField({ title });
                  toast.success("Nome offerta aggiornato");
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Modifica nome non riuscita");
                }
              }}
              data-testid="offer-title-edit"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Cliente</label>
            <select
              value={offer.customer_id || ""}
              onChange={async (e) => {
                const customer = customers.find((c) => c.id === e.target.value);
                try {
                  await saveField({ customer_id: e.target.value || null, client_name: customer?.name || "" });
                  toast.success("Cliente associato all'offerta");
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Associazione cliente non riuscita");
                }
              }}
              data-testid="offer-customer-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/40"
            >
              <option value="">Seleziona cliente…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Stato</label>
            <select value={offer.status} onChange={(e) => saveField({ status: e.target.value })} data-testid="offer-status-select"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/40">
              {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Ribasso d'asta (%)</label>
            <input type="number" defaultValue={offer.ribasso} onBlur={(e) => saveField({ ribasso: parseFloat(e.target.value) || 0 })} data-testid="offer-ribasso-field"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Oneri sicurezza (€)</label>
            <input type="number" defaultValue={offer.security_charges} onBlur={(e) => saveField({ security_charges: parseFloat(e.target.value) || 0 })} data-testid="offer-security-field"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.filter(t=>canTab(t.id)).map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? "border-orange-600 text-orange-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div>
        {canTab(tab) && tab === "file" && <FilesTab offerId={id} />}
        {canTab(tab) && tab === "analisi" && <AnalysisTab offerId={id} onTransferred={() => { setTab("cme"); refreshTotals(); }} />}
        {canTab(tab) && tab === "manifest" && <ProjectManifestTab offerId={id} />}
        {canTab(tab) && tab === "bimviewer" && <BimViewerTab offerId={id} />}
        {canTab(tab) && tab === "cme" && <CmeTab offerId={id} offer={offer} onChange={refreshTotals} />}
        {canTab(tab) && tab === "migliorie" && <ImprovementsTab offerId={id} />}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex flex-col p-4 sm:p-8" onClick={() => setPreview(false)}>
          <div className="bg-white rounded-xl w-full max-w-5xl mx-auto flex flex-col flex-1 overflow-hidden fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Eye className="w-5 h-5 text-orange-600" /> Anteprima Documento di Offerta</h3>
              <div className="flex items-center gap-2">
                <a href={api.exportPdfUrl(id)} data-testid="preview-download-button" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-3.5 py-2 rounded-lg">
                  <FileDown className="w-4 h-4" /> Scarica PDF
                </a>
                <button onClick={() => setPreview(false)} data-testid="preview-close-button" className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 relative bg-slate-100">
              {previewLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-orange-500 animate-pulse" />
                  <span className="ml-2 text-sm text-slate-500">Generazione anteprima…</span>
                </div>
              )}
              <iframe title="Anteprima PDF" src={api.exportPdfPreviewUrl(id)} data-testid="preview-pdf-iframe" onLoad={() => setPreviewLoading(false)} className="w-full h-full" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
